/* eslint-disable no-underscore-dangle */
import { createFeatureSelector, createSelector, select } from '@ngrx/store';

import { safeSelect, StateSelector } from '@fcom/core/selectors/selector-utils';
import { LocalDate } from '@fcom/core/utils';
import { DeepLinkSource } from '@fcom/common/interfaces';
import {
  FinnairAlternativeOffer,
  FinnairAlternativeOffers,
  FinnairOrder,
  FinnairSelfReacConfirmationResponse,
} from '@fcom/dapi/api/models';

import {
  ALTERNATIVE_OFFERS_STATE_FEATURE_NAME,
  AlternativeOffersState,
  AlternativeOffersStateStatus,
  BookingInfo,
  OrderSummaryStatus,
  ReaccommodationFeatureState,
  Stage,
} from '../../interfaces';

const _selectAlternativeOffersState = createFeatureSelector<ReaccommodationFeatureState, AlternativeOffersState>(
  ALTERNATIVE_OFFERS_STATE_FEATURE_NAME
);

export const _selectOffers = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.alternativeOffers?.[state.disruptedBoundIndex]
);

const _allOffers = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.alternativeOffers
);

export const _selectedOfferIds = createSelector(_selectAlternativeOffersState, (state: AlternativeOffersState) =>
  Object.values(state.selectedOffers ?? {}).filter(Boolean)
);

const _selectOffersHash = () =>
  createSelector(_selectAlternativeOffersState, (state: AlternativeOffersState) =>
    state.alternativeOffers?.[state.disruptedBoundIndex]
      ? state.alternativeOffers?.[state.disruptedBoundIndex].hash
      : ''
  );

const _selectStatus = createSelector(_selectAlternativeOffersState, (state: AlternativeOffersState) => state.status);

const _selectSelectedOfferIds = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.selectedOffers
);

const _selectDisruptedBoundIndex = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.disruptedBoundIndex
);

const _showAlternativeOffers = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.showAlternativeOffers
);

export const _selectSelectedDate = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.selectedDates
);

const _selectLocations = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.alternativeOffers?.[state.disruptedBoundIndex]?.dictionary?.locations ?? {}
);

const _selectSelectedDateOffers = createSelector(
  _selectAlternativeOffersState,
  _selectSelectedDate,
  (state: AlternativeOffersState, _selectedDate: Record<string, LocalDate>): FinnairAlternativeOffer[] => {
    const date = _selectedDate?.[state.disruptedBoundIndex];
    if (state.alternativeOffers && date) {
      try {
        return state.alternativeOffers[state.disruptedBoundIndex]?.offers[date.toString()];
      } catch (_error) {
        return [];
      }
    }
    return [];
  }
);

export const _selectSelectedOffer = createSelector(
  _selectSelectedOfferIds,
  _selectSelectedDateOffers,
  _selectDisruptedBoundIndex,
  (
    _offers: Record<string, string>,
    _selectedDateOffers: FinnairAlternativeOffer[] = [],
    _selectedDisruptedBoundIndex
  ) => {
    return _selectedDateOffers.find((offer) => offer.id === _offers?.[_selectedDisruptedBoundIndex]);
  }
);

export const _selectedOffers = createSelector(
  _selectSelectedOfferIds,
  _allOffers,
  (offerIds: Record<string, string>, currentAllOffers: Record<string, FinnairAlternativeOffers>) => {
    return Object.entries(offerIds ?? {}).reduce((all, [boundIndex, offerId]) => {
      const allBoundOffers = Object.values(currentAllOffers[boundIndex]?.offers ?? {}).flatMap(
        (currentAllBoundOffers) => currentAllBoundOffers
      );
      const offer = allBoundOffers.find((currentOffer) => currentOffer.id === offerId);
      if (offer) {
        all[boundIndex] = offer;
      }
      return all;
    }, {});
  }
);

const _selectOfferToAcknowledge = createSelector(
  _selectSelectedDateOffers,
  (_selectedDateOffers: FinnairAlternativeOffer[]) => {
    return _selectedDateOffers.find((offer) => offer.id === null);
  }
);

const _getStage = createSelector(_selectAlternativeOffersState, (state: AlternativeOffersState) => state.currentStage);

const _deeplinkSource = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.deeplinkSource
);

const _analyticsSent = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.analyticsSent
);

const _confirmationResponse = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.confirmationResponse
);

const _sessionExpired = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.sessionExpired
);

const _sessionStarted = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.sessionStartedMs
);

const _reviewConfirmed = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.reviewConfirmed
);

const _booking = createSelector(_selectAlternativeOffersState, (state: AlternativeOffersState) => state.booking);

const _sendRefreshOffersEvent = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.sendRefreshOffersEvent
);

const _alternativeConfirmUrl = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.alternativeConfirmStatusUrl
);

export const _getOrderSummaryData = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.orderSummary.orderSummaryData
);

const _getOrderSummaryStatus = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.orderSummary.orderStatus
);

export const _acknowledgeFlow = createSelector(
  _selectAlternativeOffersState,
  (state: AlternativeOffersState) => state.acknowledgeFlow
);

export const reaccommodationState = (): StateSelector<ReaccommodationFeatureState, AlternativeOffersState> =>
  select(_selectAlternativeOffersState);
export const offers = (): StateSelector<ReaccommodationFeatureState, FinnairAlternativeOffers> =>
  safeSelect(_selectOffers);
export const allOffers = (): StateSelector<ReaccommodationFeatureState, Record<string, FinnairAlternativeOffers>> =>
  safeSelect(_allOffers);
export const offersHash = (): StateSelector<ReaccommodationFeatureState, string> => safeSelect(_selectOffersHash());
export const status = (): StateSelector<ReaccommodationFeatureState, AlternativeOffersStateStatus> =>
  safeSelect(_selectStatus);
export const selectDisruptedBoundIndex = (): StateSelector<ReaccommodationFeatureState, number> =>
  safeSelect(_selectDisruptedBoundIndex);
export const showAlternativeOffers = (): StateSelector<ReaccommodationFeatureState, boolean> =>
  select(_showAlternativeOffers);
export const selectedDates = (): StateSelector<ReaccommodationFeatureState, Record<string, LocalDate>> =>
  safeSelect(_selectSelectedDate);
export const selectedOffer = (): StateSelector<ReaccommodationFeatureState, FinnairAlternativeOffer> =>
  select(_selectSelectedOffer);
export const selectedOffers = (): StateSelector<ReaccommodationFeatureState, Record<string, FinnairAlternativeOffer>> =>
  select(_selectedOffers);
export const offerToAcknowledge = (): StateSelector<ReaccommodationFeatureState, FinnairAlternativeOffer> =>
  select(_selectOfferToAcknowledge);
export const getStage = (): StateSelector<ReaccommodationFeatureState, Stage> => select(_getStage);
export const locations = (): StateSelector<ReaccommodationFeatureState, FinnairOrder['locations']> =>
  select(_selectLocations);
export const deeplinkSource = (): StateSelector<ReaccommodationFeatureState, DeepLinkSource> => select(_deeplinkSource);
export const confirmationResponse = (): StateSelector<
  ReaccommodationFeatureState,
  FinnairSelfReacConfirmationResponse
> => select(_confirmationResponse);
export const analyticsSent = (): StateSelector<ReaccommodationFeatureState, boolean> => select(_analyticsSent);
export const sessionExpired = (): StateSelector<ReaccommodationFeatureState, boolean> => select(_sessionExpired);
export const sessionStarted = (): StateSelector<ReaccommodationFeatureState, number> => select(_sessionStarted);
export const booking = (): StateSelector<ReaccommodationFeatureState, BookingInfo> => select(_booking);
export const alternativeConfirmUrl = (): StateSelector<ReaccommodationFeatureState, string> =>
  select(_alternativeConfirmUrl);
export const sendRefreshOffersEvent = (): StateSelector<ReaccommodationFeatureState, boolean> =>
  select(_sendRefreshOffersEvent);
export const orderSummaryData = (): StateSelector<ReaccommodationFeatureState, FinnairOrder> =>
  select(_getOrderSummaryData);
export const orderSummaryStatus = (): StateSelector<ReaccommodationFeatureState, OrderSummaryStatus> =>
  select(_getOrderSummaryStatus);
export const reviewConfirmed = (): StateSelector<ReaccommodationFeatureState, boolean> => select(_reviewConfirmed);
export const acknowledgeFlow = (): StateSelector<ReaccommodationFeatureState, boolean> => select(_acknowledgeFlow);
